import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  HStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  Flex,
  Button,
  Badge,
  VStack,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useDisclosure
} from '@chakra-ui/react';
import { 
  MdSearch, 
  MdPeople, 
  MdTrendingUp, 
  MdAttachMoney,
  MdCheckCircle,
  MdRefresh 
} from 'react-icons/md';
import { getToken } from '../../../../services/api';
import ComercialUserCard from './ComercialUserCard';
import UserDetailModal from './UserDetailModal';

const TeamOverview = () => {
  const [teamData, setTeamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('totalPrecatorios');
  const [selectedUser, setSelectedUser] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    fetchTeamData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchTeamData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTeamData = async () => {
    try {
      if (!loading) setRefreshing(true);
      
      const token = getToken();
      if (!token) {
        setError('Token não encontrado');
        return;
      }

      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(`${API_URL}/gestor/team-overview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar dados da equipe');
      }

      const result = await response.json();
      setTeamData(result.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching team data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    onOpen();
  };

  const handleRefresh = () => {
    fetchTeamData();
  };

  // Filter and sort data
  const filteredData = teamData
    .filter(user => 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch(sortBy) {
        case 'totalPrecatorios':
          return b.metrics.totalPrecatorios - a.metrics.totalPrecatorios;
        case 'totalValue':
          return b.metrics.totalValue - a.metrics.totalValue;
        case 'successRate':
          return b.metrics.successRate - a.metrics.successRate;
        case 'acceptanceRate':
          return b.metrics.acceptanceRate - a.metrics.acceptanceRate;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

  // Calculate team totals
  const teamTotals = teamData.reduce((acc, user) => ({
    totalPrecatorios: acc.totalPrecatorios + user.metrics.totalPrecatorios,
    totalValue: acc.totalValue + user.metrics.totalValue,
    totalProposalValue: acc.totalProposalValue + user.metrics.acceptedProposalValue,
    totalUsers: acc.totalUsers + 1
  }), { totalPrecatorios: 0, totalValue: 0, totalProposalValue: 0, totalUsers: 0 });

  const avgSuccessRate = teamData.length > 0
    ? (teamData.reduce((acc, user) => acc + user.metrics.successRate, 0) / teamData.length).toFixed(1)
    : 0;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <VStack align="start" spacing={1}>
            <Heading size="lg">Equipe Comercial</Heading>
            <Text color="gray.600">
              Acompanhe o desempenho da equipe comercial
            </Text>
          </VStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={handleRefresh}
            isLoading={refreshing}
            colorScheme="blue"
            variant="outline"
          >
            Atualizar
          </Button>
        </Flex>

        {/* Team Statistics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <Box bg={statBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <Stat>
              <StatLabel color="gray.600">
                <HStack>
                  <Icon as={MdPeople} />
                  <Text>Total de Usuários</Text>
                </HStack>
              </StatLabel>
              <StatNumber fontSize="2xl">{teamTotals.totalUsers}</StatNumber>
              <StatHelpText>Comerciais ativos</StatHelpText>
            </Stat>
          </Box>

          <Box bg={statBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <Stat>
              <StatLabel color="gray.600">
                <HStack>
                  <Icon as={MdTrendingUp} />
                  <Text>Total Precatórios</Text>
                </HStack>
              </StatLabel>
              <StatNumber fontSize="2xl">{teamTotals.totalPrecatorios}</StatNumber>
              <StatHelpText>Em gerenciamento</StatHelpText>
            </Stat>
          </Box>

          <Box bg={statBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <Stat>
              <StatLabel color="gray.600">
                <HStack>
                  <Icon as={MdAttachMoney} />
                  <Text>Valor Total</Text>
                </HStack>
              </StatLabel>
              <StatNumber fontSize="xl">{formatCurrency(teamTotals.totalValue)}</StatNumber>
              <StatHelpText>Sob gestão</StatHelpText>
            </Stat>
          </Box>

          <Box bg={statBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <Stat>
              <StatLabel color="gray.600">
                <HStack>
                  <Icon as={MdCheckCircle} />
                  <Text>Taxa de Sucesso</Text>
                </HStack>
              </StatLabel>
              <StatNumber fontSize="2xl">{avgSuccessRate}%</StatNumber>
              <StatHelpText>
                <StatArrow type={avgSuccessRate > 50 ? 'increase' : 'decrease'} />
                Média da equipe
              </StatHelpText>
            </Stat>
          </Box>
        </SimpleGrid>

        {/* Filters */}
        <HStack spacing={4} wrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <MdSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por nome ou email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <Select
            maxW="200px"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="totalPrecatorios">Qtd. Precatórios</option>
            <option value="totalValue">Valor Total</option>
            <option value="successRate">Taxa de Sucesso</option>
            <option value="acceptanceRate">Taxa de Aceitação</option>
            <option value="name">Nome</option>
          </Select>
        </HStack>

        {/* User Cards Grid */}
        {filteredData.length === 0 ? (
          <Box textAlign="center" py={10}>
            <Text color="gray.500">Nenhum usuário encontrado</Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {filteredData.map(user => (
              <ComercialUserCard
                key={user._id}
                user={user}
                onClick={() => handleUserClick(user)}
              />
            ))}
          </SimpleGrid>
        )}
      </VStack>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          isOpen={isOpen}
          onClose={onClose}
          user={selectedUser}
        />
      )}
    </Container>
  );
};

export default TeamOverview;